"use client";
import {
  type ComponentType,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type Step } from "./surveySteps";
import { Option } from "./surveySteps";
import {
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronLeft,
  CircleDollarSign,
  Clock,
  Coins,
  Languages,
  Package,
  Phone,
  Rocket,
  Search,
  ShoppingCart,
  TrendingUp,
  Zap,
} from "lucide-react";
import {
  getSurveySteps,
  postSurveyFill,
} from "../services/survey/surveyService";
import { useNavigate } from "react-router-dom";
import { dictionary } from "../utils/dictionary";
import { Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";
import {
  ONBOARDING_PHONE_COUNTRIES,
  ONBOARDING_PHONE_COUNTRY_CODES,
} from "../const/worldCountryCodes";
import { decodeTokenService } from "../services/decodeTokenService";
import {
  CURRENT_ONBOARDING_VERSION,
  LOCAL_ONBOARDING_STEPS,
  ONBOARDING_VERSION_STEP_ID,
} from "./localOnboardingSteps";
import {
  getUserSettings,
  upsertUserSettings,
  UserSettingsPayloadInterface,
} from "../services/settings/userSettings";
import { getCountriesList, getLanguagesList } from "../const/countryList";
import {
  getCurrencySymbol,
  getDefaultTimezoneForCountry,
  getTimezoneSearchText,
  getTimezoneUtcOffset,
} from "../utils/formatters";

const DEFAULT_SURVEY_COUNTRY = ONBOARDING_PHONE_COUNTRIES.find((c) => c.code === "CO")!;
const REQUIRED_PHONE_DIGITS_BY_COUNTRY: Record<string, { length: number; startsWith?: string }> = {
  CO: { length: 10, startsWith: "3" },
  MX: { length: 10 },
  US: { length: 10 },
  EC: { length: 9 },
  PE: { length: 9 },
  CL: { length: 9 },
  AR: { length: 10 },
  BO: { length: 8 },
  BR: { length: 11 },
  CA: { length: 10 },
  PY: { length: 9 },
  UY: { length: 8 },
  VE: { length: 10 },
};
const PERSONAL_INFO_STEP_IDS = new Set(["fullName", "mobelNumber", "mobileNumber"]);
const NEXT_STEP_OVERRIDES: Record<string, string> = {
  mobileNumber: "mobelNumber",
};

const currencyLabelKeys: Record<string, keyof typeof dictionary> = {
  COP: "colombian-peso",
  USD: "dollar",
  ARS: "argentine-peso",
  BOB: "boliviano",
  BRL: "brazilian-real",
  CAD: "canadian-dollar",
  CLP: "chilean-peso",
  MXN: "mexican-peso",
  PYG: "paraguayan-guarani",
  PEN: "peruvian-sol",
  UYU: "uruguayan-peso",
  VES: "venezuelan-bolivar",
  GTQ: "guatemalan-quetzal",
};

const translate = (key: string | undefined, language: Language, fallback = "") => {
  if (!key) return fallback;
  return dictionary[key]?.[language] || fallback || key;
};

const normalizeNextStepId = (stepId: string | undefined) =>
  stepId ? NEXT_STEP_OVERRIDES[stepId] || stepId : undefined;

const getOnboardingSessionKey = (userId: string, key: string) =>
  userId ? `fluxi:onboarding:${userId}:${key}` : "";

const getStoredSessionValue = (key: string) => {
  if (!key || typeof window === "undefined") return "";
  return sessionStorage.getItem(key) || "";
};

const setStoredSessionValue = (key: string, value: string) => {
  if (!key || typeof window === "undefined") return;
  sessionStorage.setItem(key, value);
};

const removeStoredSessionValue = (key: string) => {
  if (!key || typeof window === "undefined") return;
  sessionStorage.removeItem(key);
};

const hasPersonalInfoValues = (values: Record<string, unknown>) =>
  Boolean(String(values.fullName || "").trim()) &&
  Boolean(String(values.mobelNumber || values.mobileNumber || "").trim());

const normalizeAnswerAliases = (values: Record<string, unknown>) => {
  const phoneNumber = values.mobelNumber || values.mobileNumber;
  if (!phoneNumber || values.mobelNumber) return values;
  return { ...values, mobelNumber: phoneNumber };
};

const normalizeMissingStepIds = (ids: string[], skipPersonalInfo: boolean) =>
  ids
    .map(normalizeNextStepId)
    .filter((id): id is string => Boolean(id))
    .filter((id) => !skipPersonalInfo || !PERSONAL_INFO_STEP_IDS.has(id));

const normalizeStepDescription = (step: Step) => {
  if (
    step.id === "monthlySales" &&
    typeof step.description === "string" &&
    step.description.toLowerCase().includes("pesos colombianos")
  ) {
    return "";
  }

  return step.description;
};

const normalizeSurveySteps = (steps: Step[]) => {
  const normalizedSteps = steps
    .map((step) => ({
      ...step,
      id: normalizeNextStepId(step.id) || step.id,
      type: step.id === "businessModel" ? "single" : step.type,
      description: normalizeStepDescription(step),
      next_step:
        typeof step.next_step === "string"
          ? normalizeNextStepId(step.next_step)
          : step.next_step,
      options: step.options?.map((option) => ({
        ...option,
        next: normalizeNextStepId(option.next),
      })),
    }));

  const normalizedStepIds = new Set(normalizedSteps.map((step) => step.id));
  const missingRequiredLocalSteps = LOCAL_ONBOARDING_STEPS.filter(
    (step) => step.id === "monthlySales" && !normalizedStepIds.has(step.id)
  );

  if (missingRequiredLocalSteps.length === 0) return normalizedSteps;

  const summaryIndex = normalizedSteps.findIndex((step) => step.type === "summary");
  if (summaryIndex === -1) return [...normalizedSteps, ...missingRequiredLocalSteps];

  return [
    ...normalizedSteps.slice(0, summaryIndex),
    ...missingRequiredLocalSteps,
    ...normalizedSteps.slice(summaryIndex),
  ];
};

type OptionIcon = ComponentType<{ className?: string }>;

const getOptionIcon = (stepId: string, value: Option["value"]): OptionIcon => {
  const optionValue = String(value);

  if (stepId === "businessModel") {
    if (optionValue === "dropshipping") return Package;
    if (optionValue === "ecommerce") return ShoppingCart;
    if (optionValue === "own_brand") return BadgeCheck;
  }

  if (stepId === "sellingTime") {
    if (optionValue === "starting") return Rocket;
    if (optionValue === "scaling") return TrendingUp;
    if (optionValue === "power") return Zap;
  }

  if (stepId === "monthlySales") return CircleDollarSign;

  return Check;
};

interface InitialSurveyProps {
  missingStepIds?: string[];
  initialAnswers?: Record<string, unknown>;
  onComplete?: () => void;
  showAccountPreferencesInFlow?: boolean;
  useLocalOnboardingFlow?: boolean;
}

interface PersonalInfoValue {
  fullName: string;
  phoneNumber: string;
}

export default function InitialSurvey({
  missingStepIds = [],
  initialAnswers = {},
  onComplete,
  showAccountPreferencesInFlow = true,
  useLocalOnboardingFlow = false,
}: InitialSurveyProps) {
  const userId = decodeTokenService.getUserIdFromToken() || localStorage.getItem("user_id") || "";
  const personalInfoSessionKey = getOnboardingSessionKey(userId, "personal-info-complete");
  const nextStepSessionKey = getOnboardingSessionKey(userId, "next-step-after-preferences");
  const normalizedInitialAnswers = normalizeAnswerAliases(initialAnswers);
  const shouldSkipPersonalInfo =
    hasPersonalInfoValues(normalizedInitialAnswers) ||
    getStoredSessionValue(personalInfoSessionKey) === "true";
  const pendingMissingStepIds = useMemo(
    () => normalizeMissingStepIds(missingStepIds, shouldSkipPersonalInfo),
    [missingStepIds, shouldSkipPersonalInfo]
  );
  const [currentStepId, setCurrentStepId] = useState<string>("");
  const [history, setHistory] = useState<string[]>([]);
  const [surveySteps, setSurveySteps] = useState<Step[]>([]);
  const [currentStep, setCurrentStep] = useState<Step | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<string, unknown>>(normalizedInitialAnswers);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [accountPreferencesStatus, setAccountPreferencesStatus] = useState<
    "checking" | "needed" | "complete"
  >("checking");
  const [initialAccountPreferences, setInitialAccountPreferences] =
    useState<UserSettingsPayloadInterface | null>(null);
  const [showAccountPreferencesStep, setShowAccountPreferencesStep] = useState(false);
  const [accountPreferencesShownInFlow, setAccountPreferencesShownInFlow] = useState(false);
  const [nextStepAfterPreferences, setNextStepAfterPreferences] = useState<string>("");
  const { language } = useAuth();

  const isResuming = pendingMissingStepIds.length > 0;
  const shouldShowAccountPreferencesInFlow = showAccountPreferencesInFlow !== false;

  // Load all steps
  useEffect(() => {
    const setInitialSteps = (allSteps: Step[]) => {
      const normalizedSteps = normalizeSurveySteps(allSteps);
      setSurveySteps(normalizedSteps);

      // Determine first step to show
      let firstStep: Step | undefined;
      if (isResuming) {
        firstStep = normalizedSteps.find((s) => s.id === pendingMissingStepIds[0]);
      }
      if (!firstStep && shouldSkipPersonalInfo) {
        const storedNextStep = normalizeNextStepId(getStoredSessionValue(nextStepSessionKey));
        firstStep =
          normalizedSteps.find((s) => s.id === storedNextStep) ||
          normalizedSteps.find((s) => s.id === "businessModel") ||
          normalizedSteps.find((s) => !PERSONAL_INFO_STEP_IDS.has(s.id));
      }
      if (!firstStep) {
        firstStep =
          normalizedSteps.find((s: Step) => s.is_first_step) ||
          normalizedSteps.find((s: Step) => s.id === "fullName") ||
          normalizedSteps.find((s: Step) => s.id === "mobelNumber") ||
          normalizedSteps.find((s: Step) => s.id === "mobileNumber") ||
          normalizedSteps[0];
      }
      if (firstStep) {
        if (shouldShowAccountPreferencesInFlow && !PERSONAL_INFO_STEP_IDS.has(firstStep.id)) {
          setNextStepAfterPreferences(firstStep.id);
          setAccountPreferencesShownInFlow(true);
          setShowAccountPreferencesStep(true);
        }
        setCurrentStepId(firstStep.id);
      }
    };

    if (useLocalOnboardingFlow) {
      setInitialSteps(LOCAL_ONBOARDING_STEPS);
      return;
    }

    getSurveySteps("default")
      .then((steps) => {
        const allSteps = steps.data as Step[];
        setInitialSteps(allSteps.length > 0 ? allSteps : LOCAL_ONBOARDING_STEPS);
      })
      .catch((error) => {
        console.warn("Using local onboarding steps because survey API is unavailable", error);
        setInitialSteps(LOCAL_ONBOARDING_STEPS);
      });
  }, [
    isResuming,
    nextStepSessionKey,
    pendingMissingStepIds,
    shouldShowAccountPreferencesInFlow,
    shouldSkipPersonalInfo,
    useLocalOnboardingFlow,
  ]);

  useEffect(() => {
    let mounted = true;

    getUserSettings()
      .then((settings) => {
        if (!mounted) return;
        setInitialAccountPreferences(settings || null);
        const missingPreferences =
          !settings?.country ||
          !settings?.currency ||
          !settings?.language ||
          !settings?.time_zone;
        setAccountPreferencesStatus(missingPreferences ? "needed" : "complete");
      })
      .catch((error) => {
        if (!mounted) return;
        if (error?.response?.status === 404 || error?.response?.status === 400) {
          setAccountPreferencesStatus("needed");
          return;
        }
        setAccountPreferencesStatus("complete");
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Sync currentStep when currentStepId changes
  useEffect(() => {
    if (surveySteps.length > 0 && currentStepId) {
      setCurrentStep(surveySteps.find((s) => s.id === currentStepId));
    }
  }, [surveySteps, currentStepId]);

  useEffect(() => {
    if (
      !shouldShowAccountPreferencesInFlow ||
      accountPreferencesShownInFlow ||
      showAccountPreferencesStep ||
      !currentStep?.id ||
      PERSONAL_INFO_STEP_IDS.has(currentStep.id)
    ) {
      return;
    }

    setNextStepAfterPreferences(currentStep.id);
    setAccountPreferencesShownInFlow(true);
    setShowAccountPreferencesStep(true);
  }, [
    accountPreferencesShownInFlow,
    currentStep?.id,
    shouldShowAccountPreferencesInFlow,
    showAccountPreferencesStep,
  ]);

  if (!currentStep && !showAccountPreferencesStep) return null;

  const clearOnboardingSession = () => {
    removeStoredSessionValue(personalInfoSessionKey);
    removeStoredSessionValue(nextStepSessionKey);
  };

  const completeSurveyFlow = () => {
    clearOnboardingSession();
    onComplete?.();
  };

  const saveAnswers = async (newAnswers: Record<string, unknown>) => {
    try {
      setSaving(true);
      setSaveError("");
      await postSurveyFill(
        {
          entity_type: "user",
          entity_id: userId,
          answers: newAnswers,
        },
        "default"
      );
      setAnswers((currentAnswers) => ({ ...currentAnswers, ...newAnswers }));
      return true;
    } catch (err) {
      console.error("Error saving answer:", err);
      setSaveError(dictionary["onboarding-save-error"][language]);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Save a single answer to the backend immediately
  const saveAnswer = async (stepId: string, value: unknown) => {
    return saveAnswers({ [stepId]: value });
  };

  // Get the summary step
  const getSummaryStep = () =>
    surveySteps.find((s) => s.type === "summary");

  // Determine the natural next step from the chain
  const getChainNextId = (step: Step, value: unknown): string | undefined => {
    if (step.options) {
      const selectedOpt = step.options.find((opt) => opt.value === value);
      if (selectedOpt?.next) return normalizeNextStepId(selectedOpt.next);
    }
    if (typeof value === "object" && value !== null) {
      const nextStep = (value as { next?: unknown }).next;
      if (typeof nextStep === "string") return normalizeNextStepId(nextStep);
    }
    if (typeof step.next_step === "string" && step.next_step) return normalizeNextStepId(step.next_step);
    return undefined;
  };

  // Find the next unanswered step walking the chain from nextId
  const findNextUnanswered = (
    startId: string | undefined,
    answered: Set<string>,
    knownAnswers: Record<string, unknown>
  ): string | undefined => {
    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const stepsById = new Map(surveySteps.map((s) => [s.id, s]));
    let id = startId;

    // Walk the selected branch, skipping steps whose saved answers we know.
    while (id && id !== summaryId) {
      if (!answered.has(id)) return id; // found an unanswered step
      const step = stepsById.get(id);
      if (!step) break;
      id = getChainNextId(step, knownAnswers[id]);
    }

    return id; // either summaryId or undefined
  };

  const handleAnswer = async (value: unknown) => {
    if (!currentStep) return;
    const activeStep = currentStep;

    await new Promise((resolve) => setTimeout(resolve, 500));

    // 1. Save this answer to the backend immediately
    const answerWasSaved = await saveAnswer(activeStep.id, value);
    if (!answerWasSaved) return;

    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const newAnswers = { ...answers, [activeStep.id]: value };
    const newAnswered = new Set(Object.keys(newAnswers));

    // 2. Determine next step
    let nextId: string | undefined;

    if (isResuming) {
      // Resume mode: find the next missing step in the missingStepIds list
      const currentIdx = pendingMissingStepIds.indexOf(activeStep.id);
      const nextMissingId = normalizeNextStepId(
        currentIdx !== -1
          ? pendingMissingStepIds[currentIdx + 1]
          : pendingMissingStepIds.find((id) => !newAnswered.has(id))
      );

      const chainNext = getChainNextId(activeStep, value);
      nextId =
        nextMissingId ||
        findNextUnanswered(chainNext, newAnswered, newAnswers) ||
        summaryId;
    } else {
      // Full mode: follow the chain, skip answered steps
      const chainNext = getChainNextId(activeStep, value);
      nextId = findNextUnanswered(chainNext, newAnswered, newAnswers);
      if (!nextId) nextId = summaryId;
    }

    // 3. Navigate
    if (
      showAccountPreferencesInFlow &&
      !accountPreferencesShownInFlow &&
      (!nextId || !PERSONAL_INFO_STEP_IDS.has(nextId))
    ) {
      setHistory((prev) => [...prev, activeStep.id]);
      setNextStepAfterPreferences(nextId || summaryId);
      setAccountPreferencesShownInFlow(true);
      setShowAccountPreferencesStep(true);
      return;
    }

    if (nextId === summaryId || nextId === "summary") {
      // Send summary=true to mark survey as complete
      await postSurveyFill(
        { entity_type: "user", entity_id: userId, answers: { summary: true } },
        "default"
      ).catch(() => {});

      if (onComplete) {
        completeSurveyFlow();
        return;
      }

      if (summaryStep) {
        setHistory((prev) => [...prev, activeStep.id]);
        setCurrentStepId(summaryId);
      }
      return;
    }

    if (nextId) {
      setHistory((prev) => [...prev, activeStep.id]);
      setCurrentStepId(nextId);
    }
  };

  const handlePersonalInfoSubmit = async ({ fullName, phoneNumber }: PersonalInfoValue) => {
    if (!currentStep) return;
    const activeStep = currentStep;

    await new Promise((resolve) => setTimeout(resolve, 350));

    const phoneStep =
      surveySteps.find((s) => s.id === "mobelNumber") ||
      surveySteps.find((s) => s.id === "mobileNumber") ||
      surveySteps.find((s) => s.type === "phone");
    const phoneStepId = phoneStep?.id || "mobelNumber";
    const profileAnswers = {
      fullName,
      [phoneStepId]: phoneNumber,
      ...(useLocalOnboardingFlow
        ? { [ONBOARDING_VERSION_STEP_ID]: CURRENT_ONBOARDING_VERSION }
        : {}),
    };

    const personalInfoWasSaved = await saveAnswers(profileAnswers);
    if (!personalInfoWasSaved) return;

    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const newAnswers = { ...answers, ...profileAnswers };
    const newAnswered = new Set(Object.keys(newAnswers));
    const chainNext = phoneStep
      ? getChainNextId(phoneStep, phoneNumber)
      : getChainNextId(activeStep, fullName);
    const nextId = findNextUnanswered(chainNext, newAnswered, newAnswers) || summaryId;
    setStoredSessionValue(personalInfoSessionKey, "true");
    setStoredSessionValue(nextStepSessionKey, nextId);

    if (shouldShowAccountPreferencesInFlow || accountPreferencesStatus !== "complete") {
      setHistory((prev) => [...prev, activeStep.id]);
      setNextStepAfterPreferences(nextId);
      setAccountPreferencesShownInFlow(true);
      setShowAccountPreferencesStep(true);
      return;
    }

    if (nextId === summaryId || nextId === "summary") {
      await postSurveyFill(
        { entity_type: "user", entity_id: userId, answers: { summary: true } },
        "default"
      ).catch(() => {});

      if (onComplete) {
        completeSurveyFlow();
        return;
      }

      if (summaryStep) {
        setHistory((prev) => [...prev, activeStep.id]);
        setCurrentStepId(summaryId);
      }
      return;
    }

    setHistory((prev) => [...prev, activeStep.id]);
    setCurrentStepId(nextId);
  };

  const handleAccountPreferencesSubmit = async (payload: UserSettingsPayloadInterface) => {
    await upsertUserSettings(payload);

    // The WhatsApp prefix identifies the user's contact number, while these
    // preferences identify the store market. Updating global locale state here
    // remounts the survey and can send the user back to the personal-info step.
    setInitialAccountPreferences(payload);
    setAccountPreferencesStatus("complete");
    setShowAccountPreferencesStep(false);

    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    if (nextStepAfterPreferences === summaryId || nextStepAfterPreferences === "summary") {
      await postSurveyFill(
        { entity_type: "user", entity_id: userId, answers: { summary: true } },
        "default"
      ).catch(() => {});

      if (onComplete) {
        completeSurveyFlow();
        return;
      }

      if (summaryStep) {
        setCurrentStepId(summaryId);
      }
      return;
    }

    setCurrentStepId(nextStepAfterPreferences || "businessModel");
  };

  const handleBack = () => {
    if (showAccountPreferencesStep) {
      setShowAccountPreferencesStep(false);
      setHistory((prev) => prev.slice(0, -1));
      return;
    }

    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const prevStepId = newHistory.pop();
      if (prevStepId) setCurrentStepId(prevStepId);
      return newHistory;
    });
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#F5F6FA] font-['Plus_Jakarta_Sans',sans-serif] overflow-y-auto"
      data-onboarding-ui="final-polish-0721"
    >
      {/* Background Gradient - Fixed to cover entire viewport */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(62, 62, 255, 0) 0%, rgba(62, 110, 255, 0.2) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-10">
        <div className="w-full max-w-[560px] mx-auto bg-white/80 border border-white rounded-[28px] shadow-[0_18px_60px_rgba(49,71,176,0.12)] px-5 py-6 sm:px-8 sm:py-9 text-center">
          {showAccountPreferencesStep ? (
            <AccountPreferencesStep
              language={language}
              initialSettings={initialAccountPreferences}
              onSubmit={handleAccountPreferencesSubmit}
            />
          ) : currentStep?.id === "fullName" ? (
            <PersonalInfoStep
              language={language}
              onSubmit={handlePersonalInfoSubmit}
              saving={saving}
            />
          ) : (
            <>
              {currentStep?.type !== "summary" && (
                <h1 className="font-bold text-[24px] sm:text-[28px] md:text-[30px] leading-[1.12] mb-3 sm:mb-4 text-center sm:text-start text-[#111827]">
                  {translate(currentStep?.question_text, language)}
                </h1>
              )}
              {currentStep?.description && (
                <p className="text-[15px] sm:text-base text-[#3D3D3D] mb-5 sm:mb-6 text-center sm:text-start">
                  {translate(currentStep.description, language)}
                </p>
              )}

              {currentStep?.type === "single" && currentStep.options && (
                <SingleSelectStep
                  key={currentStep.id}
                  stepId={currentStep.id}
                  options={currentStep.options}
                  onSubmit={(value) => handleAnswer(value)}
                  language={language}
                  saving={saving}
                />
              )}

              {currentStep?.type === "multi" && currentStep.options && (
                <MultiSelectStep
                  options={currentStep.options}
                  onSubmit={(values) => handleAnswer(values)}
                  language={language}
                />
              )}

              {currentStep?.type === "text" && (
                <TextStep
                  stepId={currentStep.id}
                  language={language}
                  key={currentStep.id.toString()}
                  onSubmit={(value) => handleAnswer(value)}
                />
              )}

              {currentStep?.type === "textarea" && (
                <TextAreaStep
                  language={language}
                  key={currentStep.id.toString()}
                  onSubmit={(value) => handleAnswer(value)}
                />
              )}

              {currentStep?.type === "phone" && (
                <PhoneStep
                  language={language}
                  key={currentStep.id.toString()}
                  onSubmit={(value) => handleAnswer(value)}
                />
              )}

              {currentStep?.type === "summary" && (
                <SummaryStep
                  language={language}
                  onComplete={onComplete ? completeSurveyFlow : undefined}
                />
              )}
            </>
          )}

          {saveError && (
            <p role="alert" className="mt-4 text-center text-sm font-semibold text-[#D64545]">
              {saveError}
            </p>
          )}

          {!showAccountPreferencesStep && currentStep?.type !== "summary" && history.length > 0 && (
            <div
              onClick={handleBack}
              className="mt-4 sm:mt-6 flex items-center justify-center sm:justify-start gap-2 cursor-pointer hover:text-black transition-all"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              <p className="text-sm sm:text-base md:text-lg font-medium text-gray-500">
                {translate("back", language, "Volver")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SingleSelectStep({
  stepId,
  options = [],
  onSubmit,
  language,
  saving,
}: {
  stepId: string;
  options: Step["options"];
  onSubmit: (value: Option["value"]) => void;
  language: Language;
  saving: boolean;
}) {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  return (
    <div className="flex flex-col gap-3">
      {options.map((opt) => (
        <ButtonAnswer
          key={opt.value.toString()}
          onClick={() => setSelectedOption(opt)}
          option={opt}
          language={language}
          selected={selectedOption?.value === opt.value}
          icon={getOptionIcon(stepId, opt.value)}
          softLabel={stepId === "monthlySales"}
        />
      ))}
      <ButtonContinue
        onClick={() => selectedOption && onSubmit(selectedOption.value)}
        disabled={!selectedOption || saving}
        text={saving ? "Guardando..." : translate("continue", language, "Continuar")}
      />
    </div>
  );
}

const ButtonAnswer = ({
  onClick,
  option,
  language,
  selected,
  icon: Icon,
  softLabel = false,
}: {
  onClick: () => void;
  option: Option;
  language: Language;
  selected: boolean;
  icon: OptionIcon;
  softLabel?: boolean;
}) => {
  const label = translate(option.label, language);
  const description =
    option.description && translate(option.description, language);

  return (
    <button
      onClick={onClick}
      className={`
                w-full
                flex
                items-center
                gap-3
                text-left
                py-3
                sm:py-3.5
                px-3.5 
                sm:px-4
                hover:shadow-md 
                active:scale-95
                text-black 
                rounded-2xl
                border-2 
                transition-all 
                duration-200
                ${
                  selected
                    ? "bg-[#F7F8FF] text-[#111827] border-[#2F36D9] shadow-[0_10px_28px_rgba(47,54,217,0.14)]"
                    : "bg-white text-[#111827] border-[#D9DDE8] hover:border-[#2F36D9]/45"
                }
            `}
    >
      <span
        className={`flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl border ${
          selected
            ? "border-[#2F36D9]/20 bg-[#EEF2FF] text-[#2F36D9]"
            : "border-[#D9DDE8] bg-[#F7F8FF] text-[#3D3D3D]"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-base sm:text-lg leading-tight ${softLabel ? "font-medium" : "font-bold"}`}>{label}</span>
        {description && (
        <span
          className={`mt-1 block text-xs sm:text-sm leading-snug ${
            selected ? "text-[#3D3D3D]" : "text-[#3D3D3D]"
          }`}
        >
          {description}
        </span>
        )}
      </span>
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2F36D9] text-white transition-opacity ${
          selected ? "opacity-100" : "opacity-0"
        }`}
      >
          <Check className="h-3.5 w-3.5" />
      </span>
    </button>
  );
};

interface MultiSelectStepProps {
  options: Step["options"];
  onSubmit: (values: string[]) => void;
  language: Language;
}

function MultiSelectStep({
  options = [],
  onSubmit,
  language,
}: MultiSelectStepProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const toggleOption = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <div className="flex flex-col gap-2 sm:gap-3">
      {options.map((opt) => (
        <button
          key={opt.value.toString()}
          onClick={() => toggleOption(opt.value.toString())}
          className={`w-full border-2 py-4 sm:py-5 px-4 sm:px-5 rounded-2xl transition-all duration-200 active:scale-95 text-start text-base sm:text-lg font-bold hover:shadow-md ${
            selected.includes(opt.value.toString())
              ? "bg-[#F7F8FF] text-[#111827] border-[#2F36D9] shadow-[0_10px_28px_rgba(47,54,217,0.14)]"
              : "bg-white text-[#111827] border-[#D9DDE8] hover:border-[#2F36D9]/45"
          }`}
        >
          {translate(opt.label, language)}
        </button>
      ))}
      <ButtonContinue
        onClick={() => onSubmit(selected)}
        disabled={selected.length === 0}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

function PersonalInfoStep({
  onSubmit,
  language,
  saving,
}: {
  onSubmit: (value: PersonalInfoValue) => void;
  language: Language;
  saving: boolean;
}) {
  const [fullName, setFullName] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SURVEY_COUNTRY);
  const [phoneInput, setPhoneInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredCountries = ONBOARDING_PHONE_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search)
  );

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const nameLooksComplete = fullName.trim().split(/\s+/).length >= 2;
  const digitsOnly = phoneInput.replace(/\D/g, "");
  const countryRule = REQUIRED_PHONE_DIGITS_BY_COUNTRY[selectedCountry.code];
  const hasValidKnownCountryNumber = countryRule
    ? digitsOnly.length === countryRule.length &&
      (!countryRule.startsWith || digitsOnly.startsWith(countryRule.startsWith))
    : digitsOnly.length >= 7 && digitsOnly.length <= 15;
  const validationMessage = countryRule
    ? selectedCountry.code === "CO"
      ? "Para Colombia, el WhatsApp debe tener 10 dígitos y empezar por 3."
      : `El número debe tener ${countryRule.length} dígitos para ${selectedCountry.name}.`
    : "Escribe un número válido, entre 7 y 15 dígitos.";
  const showNameMessage = fullName.trim().length > 0 && !nameLooksComplete;
  const showValidationMessage = digitsOnly.length > 0 && !hasValidKnownCountryNumber;
  const phoneNumber = digitsOnly
    ? `${selectedCountry.dialCode} ${digitsOnly}`
    : "";
  const isReady = nameLooksComplete && hasValidKnownCountryNumber && !saving;

  return (
    <div className="text-left">
      <h1 className="font-extrabold text-[24px] sm:text-[28px] leading-[1.12] mb-6 text-[#111827]">
        Cuéntanos quién eres
      </h1>

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-[15px] sm:text-base font-semibold text-[#3D3D3D]">
            Tu nombre completo
          </span>
          <input
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={`w-full bg-white px-4 py-3 text-base border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2F36D9] ${
              showNameMessage ? "border-[#F05A5A]" : "border-[#D9DDE8]"
            }`}
            placeholder="¿Cómo te llamas?"
          />
          {showNameMessage && (
            <span className="text-sm font-medium text-[#D64545]">
              Escribe tu nombre y apellido.
            </span>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[15px] sm:text-base font-semibold text-[#3D3D3D]">
            WhatsApp
          </span>
          <div className="flex gap-2">
            <div ref={dropdownRef} className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                className="h-full min-w-[56px] rounded-2xl border border-[#D9DDE8] bg-white px-3 py-3 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#2F36D9]"
                aria-label="Seleccionar indicativo"
              >
                <span
                  className={`fi fi-${selectedCountry.code.toLowerCase()} rounded-sm`}
                  style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        ref={searchRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={translate("search-country-placeholder", language, "Buscar país o código...")}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F36D9]"
                      />
                    </div>
                  </div>

                  <ul className="max-h-52 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((c) => (
                        <li key={c.code}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCountry(c);
                              setDropdownOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                              c.code === selectedCountry.code ? "bg-gray-100" : ""
                            }`}
                          >
                            <span
                              className={`fi fi-${c.code.toLowerCase()} rounded-sm flex-shrink-0`}
                              style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
                            />
                            <span className="flex-1 truncate text-gray-800">{c.name}</span>
                            <span className="text-gray-400 flex-shrink-0 text-xs">{c.dialCode}</span>
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="px-4 py-4 text-sm text-gray-400 text-center">
                        {translate("no-countries-found", language, "No se encontraron países")}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            <input
              type="tel"
              autoComplete="tel-national"
              inputMode="tel"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value.replace(/[^\d\s-]/g, ""))}
              className={`w-full bg-white px-4 py-3 text-base border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2F36D9] ${
                showValidationMessage ? "border-[#F05A5A]" : "border-[#D9DDE8]"
              }`}
              placeholder="300 000 0000"
            />
          </div>
          {showValidationMessage && (
            <span className="text-sm font-medium text-[#D64545]">
              {validationMessage}
            </span>
          )}
        </label>
      </div>

      <ButtonContinue
        onClick={() => onSubmit({ fullName: fullName.trim(), phoneNumber })}
        disabled={!isReady}
        text={saving ? "Guardando..." : translate("continue", language, "Continuar")}
      />
    </div>
  );
}

function AccountPreferencesStep({
  onSubmit,
  language,
  initialSettings,
}: {
  onSubmit: (value: UserSettingsPayloadInterface) => Promise<void>;
  language: Language;
  initialSettings?: UserSettingsPayloadInterface | null;
}) {
  const onboardingMarketCodes = new Set<string>(ONBOARDING_PHONE_COUNTRY_CODES);
  const countries = getCountriesList(language).filter((country) =>
    onboardingMarketCodes.has(country.country)
  );
  const languagesList = getLanguagesList(language);
  const initialCountry = countries.find((item) => item.country === initialSettings?.country);
  const initialTimezone =
    initialSettings?.time_zone ||
    (initialCountry
      ? getDefaultTimezoneForCountry(initialCountry.country, "America/Bogota")
      : Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Bogota");
  const [countryCode, setCountryCode] = useState<string>(initialSettings?.country || "");
  const [languagePreference, setLanguagePreference] = useState<Language>(
    (initialSettings?.language as Language) || initialCountry?.language || language
  );
  const [currency, setCurrency] = useState<string>(
    initialSettings?.currency || initialCountry?.currency || "COP"
  );
  const [timezone, setTimezone] = useState<string>(
    initialTimezone
  );
  const [languageOpen, setLanguageOpen] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [preferencesError, setPreferencesError] = useState("");
  const countryRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const timezoneRef = useRef<HTMLDivElement>(null);
  const currentCountry = countries.find((item) => item.country === countryCode);
  const submitCountryCode = currentCountry?.country || initialSettings?.country || "";
  const submitCurrency = currency || initialSettings?.currency || currentCountry?.currency || "";
  const canSubmitPreferences = Boolean(submitCountryCode && submitCurrency);
  const currencyLabelKey = currencyLabelKeys[currency];
  const currencyName = currencyLabelKey
    ? dictionary[currencyLabelKey][language]
    : dictionary["local-currency"][language];
  const timezoneOptions = Array.from(
    new Set(
      countries
        .map((country) => getDefaultTimezoneForCountry(country.country, ""))
        .filter(Boolean)
    )
  );
  const changeButtonClass =
    "h-10 rounded-2xl border-2 border-[#D9DDE8] bg-[#F4F6FF] px-4 text-sm font-semibold text-[#30368F] shadow-[0_8px_18px_rgba(48,54,143,0.08)] transition-all hover:border-[#4B4BFF] hover:bg-white hover:shadow-[0_12px_24px_rgba(75,75,255,0.14)] active:scale-[0.98]";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryRef.current &&
        !countryRef.current.contains(event.target as Node)
      ) {
        setCountryOpen(false);
      }
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setLanguageOpen(false);
      }
      if (
        timezoneRef.current &&
        !timezoneRef.current.contains(event.target as Node)
      ) {
        setTimezoneOpen(false);
        setTimezoneSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!initialSettings || countryCode) return;
    const selectedCountry = countries.find((item) => item.country === initialSettings.country);
    setCountryCode(initialSettings.country);
    setCurrency(initialSettings.currency || selectedCountry?.currency || "COP");
    setLanguagePreference(
      (initialSettings.language as Language) || selectedCountry?.language || language
    );
    setTimezone(
      initialSettings.time_zone ||
        (selectedCountry
          ? getDefaultTimezoneForCountry(selectedCountry.country, "America/Bogota")
          : "America/Bogota")
    );
  }, [countries, countryCode, initialSettings, language]);

  const handleCountrySelect = (value: string) => {
    const selectedCountry = countries.find((item) => item.country === value);
    setCountryCode(value);
    if (!selectedCountry) return;
    setCurrency(selectedCountry.currency);
    setLanguagePreference(selectedCountry.language);
    setTimezone(
      getDefaultTimezoneForCountry(
        selectedCountry.country,
        timezone || "America/Bogota"
      )
    );
  };

  const handleSubmit = async () => {
    if (!canSubmitPreferences || savingPreferences) return;
    setSavingPreferences(true);
    setPreferencesError("");
    try {
      await onSubmit({
        country: submitCountryCode,
        currency: submitCurrency,
        language: languagePreference,
        time_zone: timezone,
        allow_market_update: true,
      });
    } catch (error) {
      console.error("Error saving onboarding market preferences:", error);
      setPreferencesError(dictionary["onboarding-preferences-save-error"][language]);
    } finally {
      setSavingPreferences(false);
    }
  };

  return (
    <div className="text-left">
      <h1 className="font-extrabold text-[24px] sm:text-[28px] leading-[1.12] mb-6 text-[#111827]">
        {dictionary["what-country-do-you-want-to-sell-with-fluxi"][language]}
      </h1>

      <div className="mb-4">
        <div
          ref={countryRef}
          className="relative"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setCountryOpen((open) => !open);
            }}
            className="flex h-[54px] w-full items-center justify-between rounded-2xl border-2 border-[#D9DDE8] bg-white px-4 text-left text-base font-medium text-[#111827] shadow-sm transition-colors hover:border-[#4B4BFF] focus:border-[#4B4BFF] focus:outline-none focus:ring-2 focus:ring-[#2F36D9]/20"
            aria-expanded={countryOpen}
          >
            <span className="flex min-w-0 items-center gap-3">
              {currentCountry?.icon ? (
                <currentCountry.icon width="24" height="24" />
              ) : currentCountry?.flag ? (
                <span className="text-lg">{currentCountry.flag}</span>
              ) : null}
              <span className="truncate">
                {currentCountry?.name || dictionary["select-your-country"][language]}
              </span>
            </span>
            <ChevronDown
              className={`ml-3 h-5 w-5 shrink-0 text-[#6B7280] transition-transform ${
                countryOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {countryOpen && (
            <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-[120] max-h-[320px] overflow-y-auto rounded-[18px] border border-[#E4E8F2] bg-white p-2 shadow-xl">
            {countries.map((country) => (
              <button
                key={country.country}
                type="button"
                className="flex w-full cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-left text-base font-normal text-[#374151] hover:bg-[#F7F8FF]"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleCountrySelect(country.country);
                  setCountryOpen(false);
                }}
              >
                <span className="flex items-center gap-3">
                  {country.icon ? (
                    <country.icon width="24" height="24" />
                  ) : (
                    <span className="text-lg">{country.flag}</span>
                  )}
                  {country.name}
                </span>
                {country.country === countryCode && (
                  <Check className="h-4 w-4 text-[#4B4BFF]" />
                )}
              </button>
            ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-3">
        <PreferenceCard
          icon={<Coins className="h-5 w-5" />}
          label={dictionary["currency"][language]}
          value={
            currentCountry
              ? `${currency} - ${currencyName} (${getCurrencySymbol(currentCountry.country)})`
              : submitCurrency
                ? `${submitCurrency} - ${currencyName}`
                : "Selecciona un país"
          }
          muted={!currentCountry && !submitCurrency}
        />

        <div
          ref={languageRef}
          className="relative rounded-2xl border-2 border-[#D9DDE8] bg-white p-4"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F4F6FF] text-[#3D3D3D]">
                <Languages className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#6B7280]">
                  {dictionary["language"][language]}
                </p>
                <p className="truncate text-base font-semibold text-[#111827]">
                  {languagesList.find((item) => item.code === languagePreference)?.name ||
                    languagePreference}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setLanguageOpen(true);
              }}
              className={changeButtonClass}
            >
              Cambiar
            </button>
          </div>

          {languageOpen && (
            <div className="absolute left-4 right-4 top-[calc(100%-6px)] z-[100] rounded-[18px] border border-[#E4E8F2] bg-white p-2 shadow-xl">
              {languagesList.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm hover:bg-[#F7F8FF]"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setLanguagePreference(item.code);
                    setLanguageOpen(false);
                  }}
                >
                  <span>{item.name}</span>
                  {item.code === languagePreference && (
                    <Check className="h-4 w-4 text-[#4B4BFF]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div
          ref={timezoneRef}
          className="relative rounded-2xl border-2 border-[#D9DDE8] bg-white p-4"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F4F6FF] text-[#3D3D3D]">
                <Clock className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#6B7280]">Zona horaria</p>
                <p className="truncate text-base font-semibold text-[#111827]">
                  {timezone}{" "}
                  <span className="text-sm font-normal text-[#6B7280]">
                    {getTimezoneUtcOffset(timezone)}
                  </span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setTimezoneOpen(true);
                setTimezoneSearch("");
              }}
              className={changeButtonClass}
            >
              Cambiar
            </button>
          </div>

          {timezoneOpen && (
            <div className="absolute left-4 right-4 top-[calc(100%-6px)] z-[100] rounded-[18px] border border-[#E4E8F2] bg-white p-2 shadow-xl">
              <input
                autoFocus
                className="mb-2 h-10 w-full rounded-[12px] border border-[#DCE1EE] px-3 text-sm outline-none focus:border-[#4B4BFF]"
                placeholder="Buscar zona horaria"
                value={timezoneSearch}
                onChange={(event) => setTimezoneSearch(event.target.value)}
              />
              <div className="max-h-[220px] overflow-y-auto">
                {timezoneOptions
                  .filter((item) =>
                    getTimezoneSearchText(item).includes(timezoneSearch.toLowerCase().trim())
                  )
                  .map((item) => (
                    <button
                      key={item}
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-[#F7F8FF]"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setTimezone(item);
                        setTimezoneOpen(false);
                        setTimezoneSearch("");
                      }}
                    >
                      <span className="truncate text-[#111827]">{item}</span>
                      <span className="flex shrink-0 items-center gap-2 text-xs text-[#6B7280]">
                        {getTimezoneUtcOffset(item)}
                        {item === timezone && <Check className="h-4 w-4 text-[#4B4BFF]" />}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ButtonContinue
        onClick={handleSubmit}
        disabled={!canSubmitPreferences || savingPreferences}
        text={savingPreferences ? "Guardando..." : translate("continue", language, "Continuar")}
      />
      {preferencesError && (
        <p role="alert" className="mt-3 text-center text-sm font-semibold text-[#D64545]">
          {preferencesError}
        </p>
      )}
    </div>
  );
}

function PreferenceCard({
  icon,
  label,
  value,
  muted,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="rounded-2xl border-2 border-[#D9DDE8] bg-white p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F4F6FF] text-[#3D3D3D]">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#6B7280]">{label}</p>
          <p className={`truncate text-base font-semibold ${muted ? "text-[#9CA3AF]" : "text-[#111827]"}`}>
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

interface TextAreaStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function TextAreaStep({ onSubmit, language }: TextAreaStepProps) {
  const [value, setValue] = useState("");
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <textarea
        className="border rounded-lg p-3 sm:p-4 text-sm sm:text-base w-full h-[150px] sm:h-[180px] md:h-[200px] resize-none focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          translate("write-your-answer-here", language, "Escribe tu respuesta aquí...")
        }
      />
      <ButtonContinue
        onClick={() => onSubmit(value)}
        disabled={!value.trim()}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

interface TextStepProps {
  onSubmit: (value: string) => void;
  language: Language;
  stepId?: string;
}

function TextStep({ onSubmit, language, stepId }: TextStepProps) {
  const [value, setValue] = useState("");
  const isProductQuestion = stepId?.toLowerCase().includes("product");
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <input
        type="text"
        className="border rounded-2xl p-4 text-base sm:text-lg w-full focus:outline-none focus:ring-2 focus:ring-[#2F36D9] bg-white border-[#D9DDE8]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          isProductQuestion
            ? translate(
                "product-examples-placeholder",
                language,
                "Ej.: Suplementos, calzado, cosméticos..."
              )
            : translate("write-your-answer-here", language, "Escribe tu respuesta aquí...")
        }
      />
      <ButtonContinue
        onClick={() => onSubmit(value)}
        disabled={!value.trim()}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

function SummaryStep({
  language,
  onComplete,
}: {
  language: Language;
  onComplete?: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-center justify-center bg-[#0E172A] rounded-full p-3 sm:p-4 mb-4 sm:mb-6">
        <Check className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-white" />
      </div>
      <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[58px] leading-[1.1] mb-6 sm:mb-8 text-[#0E172A] text-center">
        {translate("all-set", language, "Todo listo")}
      </h1>
      <ButtonContinue
        onClick={() => {
          if (onComplete) {
            onComplete();
            return;
          }
          if (window.innerWidth < 768) {
            navigate("/settings");
          } else {
            navigate("/home");
          }
        }}
        disabled={false}
        text={translate("go-to-account", language, "Ir a mi cuenta")}
      />
    </div>
  );
}

interface PhoneStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function PhoneStep({ onSubmit, language }: PhoneStepProps) {
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SURVEY_COUNTRY);
  const [phoneInput, setPhoneInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredCountries = ONBOARDING_PHONE_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search)
  );

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const digitsOnly = phoneInput.replace(/\D/g, "");
  const countryRule = REQUIRED_PHONE_DIGITS_BY_COUNTRY[selectedCountry.code];
  const hasValidKnownCountryNumber = countryRule
    ? digitsOnly.length === countryRule.length &&
      (!countryRule.startsWith || digitsOnly.startsWith(countryRule.startsWith))
    : digitsOnly.length >= 7 && digitsOnly.length <= 15;
  const validationMessage = countryRule
    ? selectedCountry.code === "CO"
      ? "Para Colombia, el WhatsApp debe tener 10 dígitos y empezar por 3."
      : `El número debe tener ${countryRule.length} dígitos para ${selectedCountry.name}.`
    : "Escribe un número válido, entre 7 y 15 dígitos.";
  const showValidationMessage = digitsOnly.length > 0 && !hasValidKnownCountryNumber;
  const phoneNumber = digitsOnly
    ? `${selectedCountry.dialCode} ${digitsOnly}`
    : "";

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="flex gap-2">
        {/* Country code dropdown */}
        <div ref={dropdownRef} className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            className="h-full bg-white px-3 py-3 sm:py-4 border border-gray-200 rounded-xl flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#0E172A] min-w-[64px]"
          >
            <span
              className={`fi fi-${selectedCountry.code.toLowerCase()} rounded-sm`}
              style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
              {/* Search */}
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      translate("search-country-placeholder", language, "Buscar país o código...")
                    }
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
                  />
                </div>
              </div>

              {/* Country list */}
              <ul className="max-h-52 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((c) => (
                    <li key={c.code}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCountry(c);
                          setDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                          c.code === selectedCountry.code ? "bg-gray-100" : ""
                        }`}
                      >
                        <span
                          className={`fi fi-${c.code.toLowerCase()} rounded-sm flex-shrink-0`}
                          style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
                        />
                        <span className="flex-1 truncate text-gray-800">{c.name}</span>
                        <span className="text-gray-400 flex-shrink-0 text-xs">{c.dialCode}</span>
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-4 text-sm text-gray-400 text-center">
                    {translate("no-countries-found", language, "No se encontraron países")}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Phone number input */}
        <div className="relative flex-1">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value.replace(/[^\d\s-]/g, ""))}
            className={`w-full bg-white pl-12 pr-4 py-3 sm:py-4 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2F36D9] ${
              showValidationMessage ? "border-[#F05A5A]" : "border-gray-200"
            }`}
            placeholder={
              translate("phone-number-placeholder", language, "300 123 4567")
            }
          />
        </div>
      </div>
      {showValidationMessage && (
        <p className="text-left text-sm font-medium text-[#D64545]">{validationMessage}</p>
      )}

      <ButtonContinue
        onClick={() => onSubmit(phoneNumber)}
        disabled={!hasValidKnownCountryNumber}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

const ButtonContinue = ({
  onClick,
  disabled,
  text = "Continuar",
}: {
  onClick: () => void;
  disabled: boolean;
  text?: string;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        mt-4
        inline-flex
        min-h-[56px]
        w-full
        items-center
        justify-center
        rounded-[15px]
        border
        px-5
        py-3
        text-lg
        sm:text-[22px]
        font-extrabold
        leading-none
        transition-all
        duration-200
        active:scale-[0.99]
        disabled:active:scale-100
        ${
          disabled
            ? "cursor-not-allowed border-[#E5E7EE] bg-white text-[#B9B9BD]"
            : "border-transparent bg-gradient-to-r from-[#2E378D] to-[#504CFF] text-white shadow-[0_16px_32px_rgba(41,51,130,0.2)] hover:shadow-[0_18px_38px_rgba(41,51,130,0.24)]"
        }
      `}
    >
      <span>{text}</span>
    </button>
  );
};
